import { forwardRef, useEffect, useId, useRef, useState, type ReactNode } from 'react'
import clsx from 'clsx'
import { Eye, EyeOff, Filter, Search, X } from 'lucide-react'
import { reportCard, reportCardPad, reportSectionSub, reportSectionTitle } from '../reports/ui'

export function Banner({
  tone, children, onDismiss,
}: {
  tone: 'ok' | 'error'
  children: React.ReactNode
  onDismiss?: () => void
}) {
  return (
    <div
      className={clsx(
        'flex items-center justify-between gap-3 rounded-lg px-4 py-2.5 text-sm',
        tone === 'ok' ? 'bg-emerald-50 text-emerald-800 ring-1 ring-emerald-200' : 'bg-rose-50 text-rose-800 ring-1 ring-rose-200',
      )}
      role="status"
    >
      <span>{children}</span>
      {onDismiss ? (
        <button type="button" onClick={onDismiss} className="shrink-0 text-xs font-medium opacity-70 hover:opacity-100">
          Dismiss
        </button>
      ) : null}
    </div>
  )
}

export function SectionHeader({
  title, subtitle, action,
}: {
  title: string
  subtitle?: string
  action?: React.ReactNode
}) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        <h2 className={reportSectionTitle}>{title}</h2>
        {subtitle ? <p className={reportSectionSub}>{subtitle}</p> : null}
      </div>
      {action}
    </div>
  )
}

export function AdminTable({ children }: { children: React.ReactNode }) {
  return (
    <div className="overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-black/5">
      <div className="max-h-[calc(100vh-280px)] overflow-auto">
        <table className="min-w-full divide-y divide-slate-200 text-sm">{children}</table>
      </div>
    </div>
  )
}

export function Th({ children, className }: { children?: React.ReactNode; className?: string }) {
  return (
    <th className={clsx('sticky top-0 z-10 bg-slate-50/95 px-3 py-2 text-left font-mono text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-500 backdrop-blur-sm', className)}>
      {children}
    </th>
  )
}

export function Td({ children, className, mono }: { children: React.ReactNode; className?: string; mono?: boolean }) {
  return (
    <td className={clsx('whitespace-nowrap px-3 py-2 text-slate-700', mono && 'font-mono text-xs', className)}>
      {children}
    </td>
  )
}

export const Btn = forwardRef<HTMLButtonElement, {
  children: ReactNode
  onClick?: () => void
  variant?: 'primary' | 'ghost' | 'danger'
  disabled?: boolean
  type?: 'button' | 'submit'
}>(function Btn({
  children, onClick, variant = 'primary', disabled, type = 'button',
}, ref) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled}
      onClick={onClick}
      className={clsx(
        'min-h-11 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors disabled:opacity-50',
        variant === 'primary' && 'bg-slate-900 text-white hover:bg-slate-800',
        variant === 'ghost' && 'text-slate-600 ring-1 ring-slate-200 hover:bg-slate-50',
        variant === 'danger' && 'text-rose-700 ring-1 ring-rose-200 hover:bg-rose-50',
      )}
    >
      {children}
    </button>
  )
})

export function Field({
  label, children, className,
}: {
  label: string
  children: React.ReactNode
  className?: string
}) {
  return (
    <label className={clsx('block text-sm', className)}>
      <span className="mb-1 block text-xs font-medium text-slate-600">{label}</span>
      {children}
    </label>
  )
}

export const inputCls =
  'w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500'

/** QA: "In login screen or change password or new user, please give option to show
 *  password." A plain type="password" input with a trailing show/hide toggle --
 *  every password field in Settings (New user, Change password) shares this one
 *  component so the behavior and styling stay identical across them. */
export function PasswordInput({
  value, onChange, autoComplete, autoFocus, className,
}: {
  value: string
  onChange: (value: string) => void
  autoComplete?: string
  autoFocus?: boolean
  className?: string
}) {
  const [visible, setVisible] = useState(false)
  return (
    <div className="relative">
      <input
        className={clsx(inputCls, 'pr-9', className)}
        type={visible ? 'text' : 'password'}
        value={value}
        autoComplete={autoComplete}
        autoFocus={autoFocus}
        onChange={(e) => onChange(e.target.value)}
      />
      <button
        type="button"
        onClick={() => setVisible((v) => !v)}
        aria-label={visible ? 'Hide password' : 'Show password'}
        className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
      >
        {visible ? <EyeOff size={15} /> : <Eye size={15} />}
      </button>
    </div>
  )
}

export function PasswordField({
  value, onChange, label, autoComplete, autoFocus,
}: {
  value: string
  onChange: (next: string) => void
  label: string
  autoComplete?: string
  autoFocus?: boolean
}) {
  const [show, setShow] = useState(false)
  return (
    <Field label={label}>
      <span className="relative block">
        <input
          className={clsx(inputCls, 'pr-12')}
          type={show ? 'text' : 'password'}
          value={value}
          aria-label={label}
          autoComplete={autoComplete}
          autoFocus={autoFocus}
          onChange={(e) => onChange(e.target.value)}
        />
        <button
          type="button"
          onClick={(e) => { e.preventDefault(); setShow((s) => !s) }}
          aria-label={show ? 'Hide password' : 'Show password'}
          className="absolute right-0.5 top-1/2 inline-flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-50 hover:text-slate-700"
        >
          {show ? <EyeOff size={16} aria-hidden /> : <Eye size={16} aria-hidden />}
        </button>
      </span>
    </Field>
  )
}

export function FormCard({ children }: { children: React.ReactNode }) {
  return <div className={clsx(reportCard, reportCardPad, 'space-y-4')}>{children}</div>
}

export function AdminSearch({
  value, onChange, label, placeholder,
}: {
  value: string
  onChange: (next: string) => void
  label: string
  placeholder?: string
}) {
  return (
    <div className="relative max-w-sm flex-1">
      <Search size={14} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden />
      <input
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-label={label}
        className={clsx(inputCls, 'pl-8 pr-8')}
      />
      {value ? (
        <button
          type="button"
          onClick={() => onChange('')}
          aria-label="Clear search"
          className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
        >
          <X size={14} />
        </button>
      ) : null}
    </div>
  )
}

export type FilterOption = { id: string; label: string }

export type FilterGroup = {
  id: string
  label: string
  value: string
  options: FilterOption[]
  onChange: (id: string) => void
}

/** Closed until clicked -- the Users/Access chip bars were filling the page with every
 *  option at once. The trigger reports aria-expanded/haspopup; Escape and a click outside
 *  close the panel the same way SelectMenu does. */
export function FilterMenu({
  groups, onClear,
}: {
  groups: FilterGroup[]
  onClear: () => void
}) {
  const [open, setOpen] = useState(false)
  const root = useRef<HTMLDivElement>(null)
  const panelId = useId()
  const activeCount = groups.filter((g) => g.value !== 'all').length
  const label = activeCount > 0 ? `Filter (${activeCount})` : 'Filter'

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent): void => {
      if (!root.current?.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  return (
    <div className="relative shrink-0" ref={root}>
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-controls={panelId}
          aria-label={label}
          className={clsx(
            'inline-flex h-11 min-w-[44px] items-center gap-2 rounded-lg border px-3 text-sm font-medium transition-colors',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500',
            open || activeCount > 0
              ? 'border-blue-400 bg-blue-50 text-blue-700'
              : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50',
          )}
        >
          <Filter size={16} aria-hidden />
          Filter
          {activeCount > 0 ? (
            <span className="rounded-full bg-blue-600 px-1.5 py-0.5 text-[10px] font-semibold leading-none text-white">
              {activeCount}
            </span>
          ) : null}
        </button>
        {activeCount > 0 ? (
          <button
            type="button"
            onClick={onClear}
            aria-label="Clear filters"
            title="Clear filters"
            className="inline-flex h-11 w-11 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 hover:text-slate-700"
          >
            <X size={16} />
          </button>
        ) : null}
      </div>

      {open ? (
        <div
          id={panelId}
          role="dialog"
          aria-label="Filters"
          className="absolute left-0 z-40 mt-1.5 w-[320px] overflow-hidden rounded-xl border border-slate-200/80 bg-white shadow-xl ring-1 ring-black/5"
        >
          <div className="max-h-[min(70vh,520px)] space-y-3 overflow-auto px-3 py-3">
            {groups.map((g) => (
              <fieldset key={g.id} className="min-w-0">
                <legend className="mb-1 text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-500">
                  {g.label}
                </legend>
                <div role="radiogroup" aria-label={g.label} className="grid gap-0.5">
                  {g.options.map((o) => {
                    const checked = g.value === o.id
                    return (
                      <label
                        key={o.id}
                        className={clsx(
                          'flex min-h-9 cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-sm transition-colors',
                          checked ? 'bg-blue-50 text-blue-800' : 'text-slate-700 hover:bg-slate-50',
                        )}
                      >
                        <input
                          type="radio"
                          name={`filter-${g.id}`}
                          value={o.id}
                          checked={checked}
                          onChange={() => g.onChange(o.id)}
                          className="h-4 w-4 accent-blue-600"
                        />
                        {o.label}
                      </label>
                    )
                  })}
                </div>
              </fieldset>
            ))}
          </div>
          {activeCount > 0 ? (
            <div className="border-t border-slate-200/80 px-3 py-2">
              <button
                type="button"
                onClick={onClear}
                className="text-xs font-medium text-slate-600 hover:text-slate-900"
              >
                Clear filters
              </button>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}
